import { Grid } from '@mui/material';
import { RoutalCrossIcon, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Text } from '../react-components/Text';
import { DRIVERS_LAYOUT_ID } from './DriversLayout';
import { LoadingButton, LoadingButtonProps } from './LoadingButton';

export interface ModalProps {
  open: boolean;
  id?: string;
  onClose?: () => void;
  title?: {
    text?: string;
    intlId?: string;
    intlValues?: Record<string, string | number>;
  };
  subtitle?: {
    text?: string;
    intlId?: string;
    intlValues?: Record<string, string | number>;
  };
  isLoading?: boolean;
  buttons?: Array<LoadingButtonProps>;
  onBackgroundClick?: () => void;
  portalElementId?: string;
}

const generateRandomId = () => `mobile-modal-${Math.random()}`;

export const Modal = ({
  open,
  id,
  onClose,
  title,
  subtitle,
  isLoading,
  buttons,
  portalElementId = DRIVERS_LAYOUT_ID,
  onBackgroundClick,
  children,
}: PropsWithChildren<ModalProps>) => {
  const keyRef = useRef<string>(id ?? generateRandomId());

  useEffect(() => {
    if (keyRef.current === null && keyRef.current !== id) {
      keyRef.current = id ?? generateRandomId();
    }
  }, [id]);

  if (!open) return null;

  const content = (
    <Grid
      key={keyRef.current}
      style={{
        height: `100%`,
        width: `100%`,
        backgroundColor: RoutalPalette.backgroundPrimary,
        position: `absolute`,
        inset: 0,
        zIndex: 1009,
        display: `flex`,
        flexWrap: `wrap`,
        justifyContent: `center`,
        alignItems: `center`,
      }}
      onClick={onBackgroundClick}
      className="animate__animated animate__fadeIn animate__faster"
    >
      <Grid
        container
        style={{
          backgroundColor: `${RoutalPalette.white}FF`,
          width: `90%`,
          borderRadius: `2%`,
          display: `flex`,
          flexWrap: `wrap`,
          alignItems: `center`,
          flexDirection: `column`,
          padding: `20px`,
        }}
      >
        {onClose ? (
          <Grid item style={{ display: `flex`, width: `100%`, justifyContent: `flex-end` }}>
            <RoutalCrossIcon
              onClick={!isLoading ? onClose : undefined}
              style={{ color: RoutalPalette.secondary40, height: `26px`, width: `26px` }}
            />
          </Grid>
        ) : null}

        <Grid container style={{ padding: `10px`, display: `flex`, alignItems: `center`, flexDirection: `column` }}>
          {title && (
            <>
              {title.intlId ? (
                <Text
                  variant="h6"
                  style={{ textAlign: `center`, color: RoutalPalette.primary40, fontWeight: `bold`, fontSize: `20px` }}
                  intlId={title.intlId}
                  intlValues={title.intlValues}
                />
              ) : (
                <Text
                  variant="h6"
                  style={{ textAlign: `center`, color: RoutalPalette.primary40, fontWeight: `bold`, fontSize: `20px` }}
                >
                  {title.text}
                </Text>
              )}

              <VSpacer medium />
            </>
          )}

          {subtitle && (
            <Grid style={{ marginBottom: `8px` }}>
              {subtitle.intlId ? (
                <Text
                  variant="body"
                  style={{ textAlign: `center` }}
                  intlId={subtitle.intlId}
                  intlValues={subtitle.intlValues}
                />
              ) : (
                <Text variant="body" style={{ textAlign: `center` }}>
                  {subtitle.text}
                </Text>
              )}
            </Grid>
          )}

          {children && (
            <>
              <VSpacer small />
              {children}
            </>
          )}
          <VSpacer small />

          {buttons && (
            <Grid container direction="column" style={{ width: `100%` }}>
              {buttons.map(({ style, ...buttonProps }, index) => (
                <LoadingButton key={index} style={{ ...style, height: `42px` }} {...buttonProps} />
              ))}
            </Grid>
          )}
        </Grid>
      </Grid>
    </Grid>
  );

  const element = document.getElementById(portalElementId ?? ``);
  if (element) {
    return createPortal(content, element as HTMLElement);
  }

  return content;
};
